import { isNull } from 'lodash';
import APIEndpoints from '../utils/apiEndpoints';
import AbstractService from './abstractService';

// Singleton instance
let instance = null;

const _SERVICE_UNAVAILABLE_ = new Error({ message : 'Service Unavailable' });

export default class UserService extends AbstractService {
	static setup(baseURL, options) {
		instance = new UserService(baseURL, options);
	}

	static getInstance() {
		return instance;
	}

	constructor(baseURL, options) {
		super(baseURL, options);
		this.endpoint = APIEndpoints.accounts;
		this.tokenEndpoint = APIEndpoints.token;
	}

	static getCurrent(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getCurrent(...params);
	}

	static updatePassword(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._updatePassword(...params);
	}

	static updateProfile(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._updateProfile(...params);
	}

	static uploadImage(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._uploadImage(...params);
	}

	static login(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._login(...params);
	}

	static logout(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._logout(...params);
	}

	static resetPassword(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._resetPassword(...params);
	}

	static register(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._register(...params);
	}

	static verify(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._verify(...params);
	}

	static resendCode(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._resendCode(...params);
	}

	static activate(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._activate(...params);
	}

	static updatePhoneNumber(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._updatePhoneNumber(...params);
	}

	static verifyClientSignupToken(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._verifyClientSignupToken(...params);
	}

	static getNotificationSettings(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getNotificationSettings(...params);
	}

	static saveNotificationSettings(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._saveNotificationSettings(...params);
	}

	static getAccountBalance(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getAccountBalance(...params);
	}

	static getBalanceTransactionHistory(...params) {
		if (isNull(instance)) return Promise.reject(_SERVICE_UNAVAILABLE_);

		return instance._getBalanceTransactionHistory(...params);
	}

	_getCurrent() {
		return this.get({ endpoint : this.endpoint });
	}

	_updatePassword({
		oldPassword,
		newPassword,
		validateRequest,
	}) {
		return this.post({
			endpoint : `${this.endpoint}/changepassword`,
			data     : {
				oldPassword,
				newPassword,
				validateRequest,
			},
		});
	}

	_updateProfile({
		firstName,
		lastName,
		city,
		zip,
		timeZone : TimeZoneId,
		email : organizationEmail,
		phone : cellPhone,
		street : streetAddress,
		state : stateId,
	}) {
		return this.put({
			endpoint : `${this.endpoint}/admin/profile`,
			data     : {
				firstName,
				lastName,
				organizationEmail,
				cellPhone,
				streetAddress,
				city,
				stateId,
				zip,
				TimeZoneId,
			},
		});
	}

	_uploadImage({ file }) {
		const data = new FormData();
		data.append('file', file);

		return this.post({
			endpoint : `${this.endpoint}/picture?imageType=4`,
			data,
		});
	}

	_login({
		email,
		password,
	}) {
		const data = {
			username      : email,
			password,
			Authenticated : true,
			grant_type    : 'password', // eslint-disable-line camelcase
			client_id     : 'bbfef102-8377-408c-bac6-7628b52af80d', // eslint-disable-line camelcase
		};

		// build the URL-encoded data for form submission via fetch
		const searchParamsObj = new URLSearchParams(data);

		// Request access token - skip SuperAgent as its not flexible enough in current implementation
		return new Promise((resolve, reject) => {
			fetch(`${this.baseURL}/${this.tokenEndpoint}`, {
				method : 'POST',
				cache  : 'no-cache',
				mode   : 'cors',
				body   : searchParamsObj,
			}).then((tokenResponse) => {
				// Received a non-error response
				if (tokenResponse.ok) {
					tokenResponse.json().then((body) => {
						localStorage.setItem('t', body.access_token);
						resolve(UserService.getCurrent());
					});
				// eslint-disable-next-line prefer-promise-reject-errors
				} else tokenResponse.json().then((body) => reject({ error : tokenResponse.status, body }));
			}).catch((error) => {
				// Transaction failed
				// eslint-disable-next-line prefer-promise-reject-errors
				reject({ error, body : 'Offline' });
			});
		});
	}

	_logout() {
		return this.post({ endpoint : `${this.endpoint}/logout` });
	}

	_resetPassword(email) {
		return this.get({
			endpoint    : this.endpoint,
			queryParams : { email },
		});
	}

	_register({
		firstName,
		lastName,
		email,
		address,
		city,
		stateId,
		zip,
		password,
		confirmPassword,
		permittedToCharge,
		// supervisorName,
		// supervisorPhone,
		// jurisdictionId,
		// departmentRef : departmentReferenceName,
		// departmentRefContact : departmentReferencePhoneOrEmail,
		// clientRef1 : clientReference1Name,
		// clientRefContact1 : clientReference1PhoneOrEmail,
		// clientRef2 : clientReference2Name,
		// clientRefContact2 : clientReference2PhoneOrEmail,
		// rank : officerRankId,
		// officerNumber : pONumber,
		phone : mobilePhone,
		locations : numberOfLocations,
		shifts : shiftsPerWeek,
		otherPaymentProcess : paymentByOtherDescription,
		paymentProcess : {
			cash : paymentByCash,
			check : paymentByCheck,
			direct_deposit : paymentByDirectDeposit,
			other : paymentByOther,
		},
	}) {
		return this.post({
			endpoint : `${this.endpoint}/coordinatorsignup`,
			data     : {
				firstName,
				lastName,
				email,
				mobilePhone,
				address,
				city,
				stateId,
				zip,
				password,
				confirmPassword,
				numberOfLocations,
				shiftsPerWeek,
				paymentByCash,
				paymentByCheck,
				paymentByDirectDeposit,
				paymentByOther,
				paymentByOtherDescription,
				permittedToCharge : permittedToCharge === 'yes',
				// jurisdictionId,
				// officerRankId,
				// pONumber,
				// supervisorName,
				// supervisorPhone,
				// departmentReferenceName,
				// departmentReferencePhoneOrEmail,
				// clientReference1Name,
				// clientReference1PhoneOrEmail,
				// clientReference2Name,
				// clientReference2PhoneOrEmail,
			},
		});
	}

	_verify({
		verificationCode, token,
	}) {
		return this.put({
			endpoint    : `${this.endpoint}/verifysmscode`,
			queryParams : { token },
			data        : {
				verificationCode,
				token,
			},
		});
	}

	_resendCode({ token }) {
		return this.put({
			endpoint    : `${this.endpoint}/resendcode`,
			queryParams : { token },
		});
	}

	_activate(id) {
		return this.get({
			endpoint    : `${this.endpoint}/verifyemail`,
			queryParams : { id },
		});
	}

	_updatePhoneNumber({ phone, token }) {
		return this.put({
			endpoint    : `${this.endpoint}/signup/mobilephone`,
			queryParams : { token },
			data        : {
				MobilePhone          : phone,
				SendVerificationCode : true,
			},
		});
	}

	_verifyClientSignupToken(token) {
		return this.get({
			endpoint       : `${this.endpoint}/verifyclientsignuptoken`,
			queryParams    : { token },
			allowAnonymous : true,
		});
	}

	_getNotificationSettings() {
		return this.get({
			endpoint : 'userNotificationSettings',
		});
	}

	_saveNotificationSettings(data) {
		return this.post({
			endpoint : 'userNotificationSettings',
			data,
		});
	}

	_getAccountBalance() {
		return this.get({
			endpoint : `${this.endpoint}/payments/organization/balance`,
		});
	}

	_getBalanceTransactionHistory(queryParams) {
		return this.get({
			endpoint : `${this.endpoint}/payments/organization/balancetransactions`,
			queryParams,
		});
	}
}
